import { useCallback, useEffect, useState } from 'react';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import {
  procurementApi,
  type ThreeWayMatchResult,
  type VendorBillSummary,
  type VendorPaymentSummary,
} from '@/api/procurement.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { OperationsLayout } from '@/shared/layouts/OperationsLayout';
import { PurchaseOrderSelect, SupplierSelect } from '@/components/selects';
import {
  createProductLineDraft,
  parseProductLines,
  ProductLineItemsEditor,
  type ProductLineDraft,
} from '@/components/forms/ProductLineItemsEditor';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { usePermissions } from '@/hooks/usePermissions';
import { useSupplierOptions } from '@/hooks/useSupplierOptions';
import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';
export function VendorPaymentsPage() {
  const { hasAny } = usePermissions();
  const canBill = hasAny('PROCUREMENT_VENDOR_BILL', 'PROCUREMENT_MANAGE');
  const canPay = hasAny('PROCUREMENT_VENDOR_PAY', 'PROCUREMENT_MANAGE');
  const { getLabel: getSupplierLabel } = useSupplierOptions();
  const billsPagination = usePagination();
  const paymentsPagination = usePagination();
  const [bills, setBills] = useState<VendorBillSummary[]>([]);
  const [payments, setPayments] = useState<VendorPaymentSummary[]>([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [billId, setBillId] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState<number | null>(null);
  const [matchResult, setMatchResult] = useState<ThreeWayMatchResult | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [billSupplierId, setBillSupplierId] = useState('');
  const [billPoId, setBillPoId] = useState('');
  const [billGrnId, setBillGrnId] = useState('');
  const [billInvoiceNo, setBillInvoiceNo] = useState('');
  const [billDate, setBillDate] = useState('');
  const [billDueDate, setBillDueDate] = useState('');
  const [billLines, setBillLines] = useState<ProductLineDraft[]>([
    createProductLineDraft({ quantity: '1', unitPrice: '0' }),
  ]);
  const [billSaving, setBillSaving] = useState(false);

  const loadBills = useCallback(async () => {
    setBillsLoading(true);
    try {
      const billPage = await procurementApi.listVendorBills({
        page: billsPagination.page,
        size: billsPagination.size,
      });
      setBills(billPage.content);
      billsPagination.applyPageResponse(billPage);
    } catch (err) {
      showApiError(err, 'Failed to load vendor bills');
    } finally {
      setBillsLoading(false);
    }
  }, [billsPagination.page, billsPagination.size, billsPagination]);

  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const paymentPage = await procurementApi.listVendorPayments({
        page: paymentsPagination.page,
        size: paymentsPagination.size,
      });
      setPayments(paymentPage.content);
      paymentsPagination.applyPageResponse(paymentPage);
    } catch (err) {
      showApiError(err, 'Failed to load vendor payments');
    } finally {
      setPaymentsLoading(false);
    }
  }, [paymentsPagination.page, paymentsPagination.size, paymentsPagination]);

  const load = async () => {
    await Promise.all([loadBills(), loadPayments()]);
  };

  useEffect(() => {
    void loadBills();
  }, [loadBills]);

  useEffect(() => {
    void loadPayments();
  }, [loadPayments]);

  const loadMatch = async (vendorBillId: number) => {
    setSelectedBillId(vendorBillId);
    setMatchLoading(true);
    setMatchResult(null);
    try {
      const result = await procurementApi.getMatch(vendorBillId);
      setMatchResult(result);
    } catch (err) {
      showApiError(err, 'Failed to load match status');
    } finally {
      setMatchLoading(false);
    }
  };

  const runMatch = async (vendorBillId: number) => {
    setMatchLoading(true);
    try {
      const result = await procurementApi.threeWayMatch(vendorBillId);
      setMatchResult({
        vendorBillId,
        matchType: 'THREE_WAY',
        matchStatus: result.matchStatus,
        quantityMatched: result.quantityMatched,
        priceMatched: result.priceMatched,
        taxMatched: result.taxMatched,
      });
      notify.success('Three-way match completed');
      await load();
    } catch (err) {
      showApiError(err, 'Three-way match failed');
    } finally {
      setMatchLoading(false);
    }
  };

  const createVendorBill = async () => {
    const supplierId = Number(billSupplierId);
    if (!supplierId) {
      notify.error('Select a supplier');
      return;
    }
    const itemsPayload = parseProductLines(billLines, 'price');
    if (itemsPayload.length === 0) {
      notify.error('Add at least one product line');
      return;
    }
    setBillSaving(true);
    try {
      await procurementApi.createVendorBill({
        supplierId,
        purchaseOrderId: billPoId ? Number(billPoId) : undefined,
        goodsReceiptId: billGrnId ? Number(billGrnId) : undefined,
        supplierInvoiceNumber: billInvoiceNo || undefined,
        billDate: billDate || undefined,
        dueDate: billDueDate || undefined,
        items: itemsPayload,
      });
      notify.success('Vendor bill created');
      setBillInvoiceNo('');
      setBillDate('');
      setBillDueDate('');
      setBillLines([createProductLineDraft({ quantity: '1', unitPrice: '0' })]);
      await load();
    } catch (err) {
      showApiError(err, 'Create vendor bill failed');
    } finally {
      setBillSaving(false);
    }
  };

  const createAndPostPayment = async () => {
    const selectedBill = bills.find((b) => b.id === Number(billId));
    if (!selectedBill) {
      notify.error('Select a valid vendor bill ID');
      return;
    }
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      notify.error('Enter valid amount');
      return;
    }
    setLoading(true);
    try {
      const draft = await procurementApi.createVendorPayment({
        supplierId: selectedBill.supplierId,
        vendorBillId: selectedBill.id,
        amount: numericAmount,
      });
      await procurementApi.postVendorPayment(draft.id);
      notify.success('Vendor payment posted');
      await load();
    } catch (err) {
      showApiError(err, 'Payment posting failed');
    } finally {
      setLoading(false);
    }
  };

  const matchBadge = (ok: boolean) => (
    <span className={ok ? 'text-emerald-600' : 'text-amber-600'}>{ok ? 'Yes' : 'No'}</span>
  );

  return (
    <OperationsLayout
      moduleLabel="Procurement"
      title="Vendor Bills & Payments"
      description="Post vendor payments with bill allocations and payable tracking."
    >
      <div className="card mb-4 space-y-4 p-4">
        <h3 className="font-semibold text-sm">Create vendor bill</h3>
        {canBill ? (
          <>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <SupplierSelect label="Supplier *" value={billSupplierId} onChange={setBillSupplierId} required />
              <PurchaseOrderSelect label="PO (optional)" value={billPoId} onChange={setBillPoId} supplierId={billSupplierId || undefined} />
              <Input label="GRN ID (optional)" value={billGrnId} onChange={(e) => setBillGrnId(e.target.value)} />
              <Input label="Supplier invoice #" value={billInvoiceNo} onChange={(e) => setBillInvoiceNo(e.target.value)} />
              <DatePicker label="Bill date" value={billDate} onChange={setBillDate} />
              <DatePicker label="Due date" value={billDueDate} onChange={setBillDueDate} />
            </div>
            <ProductLineItemsEditor lines={billLines} onChange={setBillLines} priceMode="price" />
            <Button loading={billSaving} onClick={() => void createVendorBill()}>
              Create vendor bill
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-500">Vendor bill permission required.</p>
        )}
      </div>

      <div className="card mb-4 p-4">
        <h3 className="font-semibold text-sm mb-3">Post vendor payment</h3>
        {canPay ? (
          <div className="grid gap-2 sm:grid-cols-3">
            <SearchableSelect
              label="Vendor bill"
              value={billId}
              onChange={setBillId}
              options={bills.map((b) => ({
                value: String(b.id),
                label: `${b.billNumber} · ${getSupplierLabel(b.supplierId, true)} · ${b.totalAmount}`,
              }))}
              placeholder="Select vendor bill"
              loading={billsLoading}
              searchPlaceholder="Search bills…"
            />
            <Input label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <Button className="self-end" loading={loading} onClick={() => void createAndPostPayment()}>
              Create + Post Payment
            </Button>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Vendor payment permission required.</p>
        )}
      </div>

      {selectedBillId != null && (
        <div className="card mb-4 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">Three-way match — bill #{selectedBillId}</h3>
            <Button
              variant="secondary"
              className="!px-2 !py-1 text-xs"
              loading={matchLoading}
              disabled={!canBill}
              onClick={() => void runMatch(selectedBillId)}
            >
              Run match
            </Button>
          </div>
          {matchLoading && <p className="text-sm text-slate-500">Loading match status…</p>}
          {!matchLoading && matchResult == null && (
            <p className="text-sm text-slate-500">No match record yet — run three-way match.</p>
          )}
          {!matchLoading && matchResult && (
            <div className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <p>
                Status: <strong>{matchResult.matchStatus}</strong>
              </p>
              <p>Quantity: {matchBadge(matchResult.quantityMatched)}</p>
              <p>Price: {matchBadge(matchResult.priceMatched)}</p>
              <p>Tax: {matchBadge(matchResult.taxMatched)}</p>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card overflow-x-auto">
          <div className="border-b border-slate-200 px-4 py-3 font-semibold dark:border-slate-700">Vendor Bills</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left dark:border-slate-700">
                <th className="p-3">Bill</th>
                <th className="p-3">Supplier</th>
                <th className="p-3">Status</th>
                <th className="p-3">Due</th>
                <th className="p-3">Paid/Total</th>
                <th className="p-3">Match</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-3 font-medium">{b.billNumber}</td>
                  <td className="p-3">{getSupplierLabel(b.supplierId, true)}</td>
                  <td className="p-3">{b.billStatus}</td>
                  <td className="p-3">{b.dueDate ?? '-'}</td>
                  <td className="p-3">
                    {b.paidAmount} / {b.totalAmount}
                  </td>
                  <td className="p-3">
                    <Button
                      className="!px-2 !py-1 text-xs"
                      variant="secondary"
                      onClick={() => void loadMatch(b.id)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            page={billsPagination.page}
            totalPages={billsPagination.totalPages}
            totalElements={billsPagination.totalElements}
            onPageChange={billsPagination.setPage}
            loading={billsLoading}
            hasRows={bills.length > 0}
          />
        </div>

        <div className="card overflow-x-auto">
          <div className="border-b border-slate-200 px-4 py-3 font-semibold dark:border-slate-700">Vendor Payments</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left dark:border-slate-700">
                <th className="p-3">Payment #</th>
                <th className="p-3">Status</th>
                <th className="p-3">Bill ID</th>
                <th className="p-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-3">{p.paymentNumber}</td>
                  <td className="p-3">{p.paymentStatus}</td>
                  <td className="p-3">{p.vendorBillId ?? '-'}</td>
                  <td className="p-3">{p.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            page={paymentsPagination.page}
            totalPages={paymentsPagination.totalPages}
            totalElements={paymentsPagination.totalElements}
            onPageChange={paymentsPagination.setPage}
            loading={paymentsLoading}
            hasRows={payments.length > 0}
          />
        </div>
      </div>
    </OperationsLayout>
  );
}
